import ReactGA from 'react-ga4';

// TypeScript interfaces for event parameters
export interface BaseEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
}

export interface NavigationEventParams extends BaseEventParams {
  link_text: string;
  destination: string;
}

export interface ModalEventParams extends BaseEventParams {
  trigger_location?: 'navbar' | 'hero' | 'banner' | 'getting-started';
}

export interface FormEventParams extends BaseEventParams {
  field_name?: string;
  form_type?: 'priority_access' | 'contact' | 'careers';
  error_type?: string;
}

export interface FAQEventParams extends BaseEventParams {
  question_text: string;
  item_index: number;
}

export interface UseCaseEventParams extends BaseEventParams {
  from_tab?: string;
  to_tab?: string;
  tab_name: string;
}

export interface FooterEventParams extends BaseEventParams {
  contact_type?: 'email' | 'phone';
  platform?: string;
  link_text?: string;
  destination?: string;
}

// Analytics class for managing Google Analytics
export class Analytics {
  private static isInitialized = false;

  // Initialize Google Analytics
  static initialize(): void {
    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;

    if (!measurementId) {
      console.warn('GA Measurement ID not found in environment variables');
      return;
    }

    if (!this.isInitialized) {
      ReactGA.initialize(measurementId, {
        testMode: import.meta.env.DEV, // Enable test mode in development
      });
      this.isInitialized = true;
      console.log('Google Analytics initialized with ID:', measurementId);
    }
  }

  // Track page views
  static trackPageView(path: string, title?: string): void {
    if (!this.isInitialized) {
      this.initialize();
    }

    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });

    console.log('Page view tracked:', { path, title });
  }

  // Generic event tracking
  static trackEvent(
    eventName: string,
    parameters: Record<string, any> = {}
  ): void {
    if (!this.isInitialized) {
      this.initialize();
    }

    ReactGA.event({
      action: eventName,
      category: parameters.event_category || 'User Interaction',
      label: parameters.event_label,
      value: parameters.value,
      ...parameters,
    });

    console.log('Event tracked:', { eventName, parameters });
  }

  // Navigation events
  static trackNavigation(
    eventName: string,
    params: NavigationEventParams
  ): void {
    this.trackEvent(eventName, {
      ...params,
      event_category: 'Navigation',
    });
  }

  // Modal events
  static trackModal(eventName: string, params: ModalEventParams): void {
    this.trackEvent(eventName, {
      ...params,
      event_category: 'Modal',
    });
  }

  // Form events
  static trackForm(eventName: string, params: FormEventParams): void {
    this.trackEvent(eventName, {
      ...params,
      event_category: 'Form',
    });
  }

  // FAQ events
  static trackFAQ(eventName: string, params: FAQEventParams): void {
    this.trackEvent(eventName, {
      ...params,
      event_category: 'FAQ',
    });
  }

  // Use case events
  static trackUseCase(eventName: string, params: UseCaseEventParams): void {
    this.trackEvent(eventName, {
      ...params,
      event_category: 'Use Cases',
    });
  }

  // Footer events
  static trackFooter(eventName: string, params: FooterEventParams): void {
    this.trackEvent(eventName, {
      ...params,
      event_category: 'Footer',
    });
  }

  // Scroll tracking
  static trackScrollDepth(percentage: number): void {
    this.trackEvent('scroll_depth', {
      event_category: 'Engagement',
      event_label: `${percentage}%`,
      value: percentage,
    });
  }

  // Error tracking
  static trackError(
    errorType: string,
    errorMessage: string,
    location?: string
  ): void {
    this.trackEvent('error_occurred', {
      event_category: 'Error',
      event_label: errorType,
      error_message: errorMessage,
      error_location: location,
    });
  }
}

// Export default instance
export default Analytics;
